import { useMemo, useState } from 'react'

import DualAxisBarChart from '@/components/view/atoms/chart/DualAxisBarChart'
import ListHeaderSortButton from '@/components/view/atoms/icon/ListHeaderSortButton'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'

type MetricRow = {
  group: string
  label: string
  total_recommendations: number
  clicked: number
  ctr: number
}

type SortKey = 'label' | 'total_recommendations' | 'clicked' | 'ctr'
type SortDir = 'asc' | 'desc'

interface Props {
  title: string
  data: Record<string, { total_recommendations: number; clicked: number; ctr: number }>
  formatLabel?: (key: string) => string
  emptyText?: string
  emptyHint?: string
}

// 凡例ドット＋ラベル
const LegendItem = ({ color, label }: { color: string; label: string }) => (
  <div className='flex items-center gap-2'>
    <span className='size-2' style={{ backgroundColor: color }} />
    <Text size={12}>{label}</Text>
  </div>
)

const AnalyticsResultCard = ({
  title,
  data,
  formatLabel,
  emptyText = 'データがまだありません',
  emptyHint
}: Props) => {
  const [sortKey, setSortKey] = useState<SortKey>('total_recommendations')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const rows = useMemo<MetricRow[]>(
    () =>
      Object.entries(data).map(([group, v]) => ({
        group,
        label: formatLabel ? formatLabel(group) : group,
        total_recommendations: v.total_recommendations,
        clicked: v.clicked,
        ctr: v.ctr
      })),
    [data, formatLabel]
  )

  const sortedRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      if (sortKey === 'label') return a.label.localeCompare(b.label, 'ja')
      return a[sortKey] - b[sortKey]
    })
    return sortDir === 'asc' ? sorted : sorted.reverse()
  }, [rows, sortKey, sortDir])

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'label' ? 'asc' : 'desc')
    }
  }

  const isEmpty = rows.length === 0

  return (
    <div className='bg-surface w-full overflow-hidden rounded-2xl shadow-[0px_0px_0px_1px_rgba(0,0,0,0.08)]'>
      {/* チャートエリア */}
      <div className='p-4'>
        <div className='bg-surface-secondary flex flex-col gap-2 rounded-lg px-4 py-3'>
          <div className='flex items-center gap-2'>
            <div className='flex-1'>
              <Text size={14} weight='bold'>
                {title}
              </Text>
            </div>
            <div className='flex items-center gap-4'>
              <LegendItem color='#6ECBFA' label='レコメンド総数' />
              <LegendItem color='#FFB800' label='クリック率' />
            </div>
          </div>
          {isEmpty ? (
            <div className='flex h-60 flex-col items-center justify-center gap-1'>
              <Text size={14} color={TextColor.SECONDARY}>
                {emptyText}
              </Text>
              {emptyHint && (
                <Text size={12} color={TextColor.SECONDARY}>
                  {emptyHint}
                </Text>
              )}
            </div>
          ) : (
            <div className='h-90 w-full'>
              <DualAxisBarChart
                labels={rows.map((r) => r.label)}
                leftBar={{
                  title: 'レコメンド総数',
                  values: rows.map((r) => r.total_recommendations)
                }}
                rightBar={{ title: 'クリック率', values: rows.map((r) => r.ctr) }}
                showLegend={false}
              />
            </div>
          )}
        </div>
      </div>

      {/* テーブル */}
      {!isEmpty && (
        <div className='border-primary border-t'>
          <table className='w-full table-fixed'>
            <thead>
              <TableRow head>
                <TableHeadItem type='thin'>
                  <ListHeaderSortButton
                    text='グループ'
                    order={sortKey === 'label' ? sortDir : undefined}
                    onChange={() => handleSort('label')}
                  />
                </TableHeadItem>
                <TableHeadItem type='thin' width={160}>
                  <div className='flex justify-end'>
                    <ListHeaderSortButton
                      text='レコメンド数'
                      order={sortKey === 'total_recommendations' ? sortDir : undefined}
                      onChange={() => handleSort('total_recommendations')}
                    />
                  </div>
                </TableHeadItem>
                <TableHeadItem type='thin' width={160}>
                  <div className='flex justify-end'>
                    <ListHeaderSortButton
                      text='クリック数'
                      order={sortKey === 'clicked' ? sortDir : undefined}
                      onChange={() => handleSort('clicked')}
                    />
                  </div>
                </TableHeadItem>
                <TableHeadItem type='thin' width={160}>
                  <div className='flex justify-end'>
                    <ListHeaderSortButton
                      text='クリック率'
                      order={sortKey === 'ctr' ? sortDir : undefined}
                      onChange={() => handleSort('ctr')}
                    />
                  </div>
                </TableHeadItem>
              </TableRow>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <TableRow key={row.group}>
                  <TableRowItem type='middle'>
                    <Text truncate>{row.label}</Text>
                  </TableRowItem>
                  <TableRowItem type='middle' width={160}>
                    <div className='flex justify-end pr-3'>
                      <Text>{row.total_recommendations.toLocaleString()}</Text>
                    </div>
                  </TableRowItem>
                  <TableRowItem type='middle' width={160}>
                    <div className='flex justify-end pr-3'>
                      <Text>{row.clicked.toLocaleString()}</Text>
                    </div>
                  </TableRowItem>
                  <TableRowItem type='middle' width={160}>
                    <div className='flex justify-end pr-3'>
                      <Text>{`${(row.ctr * 100).toFixed(2)}%`}</Text>
                    </div>
                  </TableRowItem>
                </TableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default AnalyticsResultCard
